import { useMemo } from 'react';
import { isEmpty } from 'lodash';

export const SHARED_STATUS = {
  PRIVATE: 0,
  ORGANIZATION: 1,
  PUBLIC: 2,
  PUBLIC_AFFILIATED: 3
};

export const PERMISSION_TYPES = {
  OWNER: 0,
  EDITOR: 1,
  VIEWER: 2
};

export const PERMISSION_OPTIONS = [
  { value: PERMISSION_TYPES.OWNER, label: 'Owner' },
  { value: PERMISSION_TYPES.EDITOR, label: 'Editor' },
  { value: PERMISSION_TYPES.VIEWER, label: 'Viewer' }
];

export const MIN_OWNER_COUNT = 1;

/**
 * Custom hook to determine user permissions for a collection
 * @param {Object} collection - The collection object with permissions
 * @param {string|number} userId - The current user's ID
 * @returns {Object} - Object containing isOwner and isEditor booleans
 */
export const useCollectionPermissions = (collection, userId) => {
  /**
   * Determine if the current user is an owner of the collection
   */
  const isOwner = useMemo(() => {
    if (!collection || isEmpty(collection?.permissions)) {
      return false;
    }
    const ownerUserIds = collection?.permissions?.owners?.map((owner) => owner.userId) || [];
    return ownerUserIds.includes(userId);
  }, [collection, userId]);

  /**
   * Determine if the current user is an editor of the collection
   */
  const isEditor = useMemo(() => {
    if (!collection || isEmpty(collection?.permissions)) {
      return false;
    }
    const editorUserIds = collection?.permissions?.editors?.map((editor) => editor.userId) || [];
    return editorUserIds.includes(userId);
  }, [collection, userId]);

  /**
   * Determine if the user can edit (is either owner or editor)
   */
  const canEdit = useMemo(() => isOwner || isEditor, [isOwner, isEditor]);

  return {
    isOwner,
    isEditor,
    canEdit
  };
};

export default useCollectionPermissions;
